import type { AddonUploadResponse, AddonUploadSource, AddonUploadView } from '@thottbot/shared';

/** What one attempt at `POST /api/addon/uploads` came back with, without HTTP details. */
export type UploadOutcome =
  | { kind: 'accepted' | 'duplicate'; upload: AddonUploadView }
  | { kind: 'rejected'; code: string; message: string }
  | { kind: 'rate_limited'; retryAfterMs: number | null }
  | { kind: 'unauthorized' }
  | { kind: 'error'; message: string };

export interface UploadOptions {
  onProgress?: (percent: number) => void;
  filename?: string;
}

/**
 * Multipart upload of one file (the addon's SavedVariables or a client cache file; the server
 * tells them apart) through XMLHttpRequest (fetch has no upload progress). `source` tells the
 * server whether the manual page or the folder watcher sent it.
 */
export function uploadAddonFile(
  blob: Blob,
  source: AddonUploadSource,
  opts: UploadOptions = {},
): Promise<UploadOutcome> {
  return new Promise((resolve) => {
    const form = new FormData();
    form.append('file', blob, opts.filename ?? 'Thottbot.lua');
    form.append('source', source);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/addon/uploads');
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) opts.onProgress?.(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => resolve(outcomeOf(xhr));
    xhr.onerror = () => resolve({ kind: 'error', message: 'Upload failed (network error)' });
    xhr.send(form);
  });
}

export interface MultiUploadOptions {
  /** Progress of the file at `index`, in percent. */
  onProgress?: (index: number, percent: number) => void;
  /** Called as each file's outcome lands, in order; a rejection does not stop the rest. */
  onResult?: (index: number, outcome: UploadOutcome) => void;
}

/**
 * Upload several files one after another (the server processes one queue anyway, and a
 * rejection of one file must not hide the others). Resolves with one outcome per file.
 */
export async function uploadAddonFiles(
  files: File[],
  source: AddonUploadSource,
  opts: MultiUploadOptions = {},
): Promise<UploadOutcome[]> {
  const outcomes: UploadOutcome[] = [];
  for (const [index, file] of files.entries()) {
    const outcome = await uploadAddonFile(file, source, {
      filename: file.name,
      onProgress: (percent) => opts.onProgress?.(index, percent),
    });
    outcomes.push(outcome);
    opts.onResult?.(index, outcome);
  }
  return outcomes;
}

function outcomeOf(xhr: XMLHttpRequest): UploadOutcome {
  let body: unknown = null;
  try {
    body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
  } catch {
    body = null;
  }
  const status = xhr.status;
  if (status === 202 || status === 200) {
    const res = body as AddonUploadResponse;
    return { kind: res.duplicate ? 'duplicate' : 'accepted', upload: res.upload };
  }
  const err = (body ?? {}) as { error?: string; message?: string };
  if (status === 401) return { kind: 'unauthorized' };
  if (status === 429) {
    const retry = Number(xhr.getResponseHeader('Retry-After'));
    return {
      kind: 'rate_limited',
      retryAfterMs: Number.isFinite(retry) && retry > 0 ? retry * 1000 : null,
    };
  }
  if (status === 400 || status === 413) {
    return {
      kind: 'rejected',
      code: err.error ?? 'rejected',
      message: err.message ?? err.error ?? `Upload refused (${status})`,
    };
  }
  return { kind: 'error', message: err.message ?? `Upload failed (${status})` };
}
